// RadicalMoveDialog.jsx
import React, { useCallback, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {
    CARD_RANKS,
    CARD_SUITS,
    Icons,
    POSITION_BASES,
    POSITION_DIGITS,
    codeToNumeric,
    codeToPictogram,
    getCardInfoFromText,
    getCardKeyFromText,
    getCardTextColor,
    getLetters,
    hasAssignedCard,
} from "./RadicalHelpers";

const getPositionScore = (position, target) => {
    if (position === target) {
        return 0;
    }

    if (position === target + 1) {
        return 1;
    }

    if (position === target - 1) {
        return 2;
    }

    return Math.abs(position - target) * 10 + (position < target ? 3 : 0);
};

const RadicalMoveDialog = ({
    open,
    onClose,
    rows,
    cardsByCode,
    currentPictograms,
    safePackets,
}) => {
    const [selectedRank, setSelectedRank] = useState("");
    const [selectedSuit, setSelectedSuit] = useState("");
    const [selectedMarkA, setSelectedMarkA] = useState("");
    const [selectedMarkB, setSelectedMarkB] = useState("");
    const [positionBase, setPositionBase] = useState("00");
    const [positionUnits, setPositionUnits] = useState("1");
    const [countMode, setCountMode] = useState("topDorso");
    const [isSimulationOpen, setIsSimulationOpen] = useState(false);

    const targetPosition = useMemo(
        () => String(parseInt(positionBase, 10) + parseInt(positionUnits, 10)),
        [positionBase, positionUnits],
    );

    const markLetters = useMemo(() => getLetters(safePackets), [safePackets]);
    const markCount = Math.min(2, rows[0]?.finalCode?.length || 2);

    const setExactCardFromKey = useCallback(
        (rank, suit) => {
            if (!rank || !suit) {
                return;
            }

            const row = rows.find(
                (item) =>
                    getCardKeyFromText(cardsByCode[item.finalCode]) ===
                    `${rank}${suit}`,
            );

            setSelectedMarkA(row?.finalCode?.[0] || "");
            setSelectedMarkB(row?.finalCode?.[1] || "");
        },
        [rows, cardsByCode],
    );

    const setExactCardFromCode = useCallback(
        (code) => {
            if (!code || code.length < markCount) {
                return;
            }

            const info = getCardInfoFromText(cardsByCode[code]);

            if (!info) {
                return;
            }

            setSelectedRank(info.rank);
            setSelectedSuit(info.suit);
        },
        [cardsByCode, markCount],
    );

    const handleRankChange = (_, value) => {
        if (value === null) {
            return;
        }

        setSelectedRank(value);

        if (value && selectedSuit) {
            setExactCardFromKey(value, selectedSuit);
        }
    };

    const handleSuitChange = (_, value) => {
        if (value === null) {
            return;
        }

        setSelectedSuit(value);

        if (selectedRank && value) {
            setExactCardFromKey(selectedRank, value);
        }
    };

    const handleMarkAChange = (_, value) => {
        if (value === null) {
            return;
        }

        setSelectedMarkA(value);

        if (value && selectedMarkB) {
            setExactCardFromCode(`${value}${selectedMarkB}`);
        }
    };

    const handleMarkBChange = (_, value) => {
        if (value === null) {
            return;
        }

        setSelectedMarkB(value);

        if (selectedMarkA && value) {
            setExactCardFromCode(`${selectedMarkA}${value}`);
        }
    };

    const result = useMemo(() => {
        const target = parseInt(targetPosition, 10);
        const countedRows = [...rows].sort((a, b) => a.position - b.position);
        if (countMode !== "topCara") {
            countedRows.reverse();
        }

        const countedIndexByCode = countedRows.reduce((acc, row, index) => {
            acc[row.finalCode] = index;
            return acc;
        }, {});

        const assignedRows = countedRows.filter((row) =>
            hasAssignedCard(cardsByCode[row.finalCode]),
        );

        if (
            Number.isNaN(target) ||
            target < 1 ||
            target > assignedRows.length
        ) {
            return {
                error: `La posición debe estar entre 1 y ${assignedRows.length}.`,
            };
        }

        const sourceRows = assignedRows.filter((row) => {
            const info = getCardInfoFromText(cardsByCode[row.finalCode]);

            if (selectedRank && info?.rank !== selectedRank) {
                return false;
            }

            if (selectedSuit && info?.suit !== selectedSuit) {
                return false;
            }

            if (selectedMarkA && row.finalCode[0] !== selectedMarkA) {
                return false;
            }

            if (selectedMarkB && row.finalCode[1] !== selectedMarkB) {
                return false;
            }

            return true;
        });

        if (sourceRows.length === 0) {
            return {
                error: "No hay ninguna carta que coincida con esos datos.",
            };
        }

        const sourcePositionByCode = assignedRows.reduce((acc, row, index) => {
            acc[row.finalCode] = index + 1;
            return acc;
        }, {});

        const buildOutcome = (sourceRow, destinationRow) => {
            const otherRows = assignedRows.filter(
                (row) => row.finalCode !== sourceRow.finalCode,
            );

            const beforeCount = otherRows.filter((row) => {
                return (
                    countedIndexByCode[row.finalCode] <
                    countedIndexByCode[destinationRow.finalCode]
                );
            }).length;

            const firstPosition = beforeCount + 1;
            const occupiedBy =
                destinationRow.finalCode === sourceRow.finalCode
                    ? ""
                    : cardsByCode[destinationRow.finalCode] || "";

            const hasCollision = hasAssignedCard(occupiedBy);
            const positions = hasCollision
                ? [firstPosition, firstPosition + 1]
                : [firstPosition];

            return {
                sourceRow,
                sourceText: cardsByCode[sourceRow.finalCode],
                sourceCode: sourceRow.finalCode,
                sourcePosition: sourcePositionByCode[sourceRow.finalCode],
                firstPosition,
                lastPosition: positions[positions.length - 1],
                occupiedBy: hasCollision ? occupiedBy : "",
                positions,
                bestScore: Math.min(
                    ...positions.map((position) =>
                        getPositionScore(position, target),
                    ),
                ),
                worstScore: Math.max(
                    ...positions.map((position) =>
                        getPositionScore(position, target),
                    ),
                ),
            };
        };

        const candidates = countedRows.map((row) => {
            const outcomes = sourceRows.map((sourceRow) =>
                buildOutcome(sourceRow, row),
            );

            const possiblePositions = [
                ...new Set(outcomes.flatMap((outcome) => outcome.positions)),
            ].sort((a, b) => a - b);

            return {
                row,
                outcomes,
                possiblePositions,
                exactCount: outcomes.filter((outcome) =>
                    outcome.positions.includes(target),
                ).length,
                afterOneCount: outcomes.filter((outcome) =>
                    outcome.positions.includes(target + 1),
                ).length,
                beforeOneCount: outcomes.filter((outcome) =>
                    outcome.positions.includes(target - 1),
                ).length,
                averageScore:
                    outcomes.reduce((sum, outcome) => {
                        return sum + outcome.bestScore;
                    }, 0) / outcomes.length,
                worstScore: Math.max(
                    ...outcomes.map((outcome) => outcome.worstScore),
                ),
                destinationDistance: Math.min(
                    ...possiblePositions.map((position) =>
                        Math.abs(position - target),
                    ),
                ),
            };
        });

        const candidate =
            sourceRows.length === 1
                ? (() => {
                      const freeCandidate = candidates.find((item) => {
                          const outcome = item.outcomes[0];

                          return (
                              !outcome.occupiedBy &&
                              outcome.firstPosition === target
                          );
                      });

                      const collisionCandidate = candidates.find((item) => {
                          const outcome = item.outcomes[0];

                          return (
                              outcome.occupiedBy &&
                              outcome.firstPosition === target
                          );
                      });

                      const fallbackCandidate = candidates.reduce(
                          (best, item) => {
                              if (
                                  !best ||
                                  item.destinationDistance <
                                      best.destinationDistance
                              ) {
                                  return item;
                              }

                              if (
                                  item.destinationDistance ===
                                      best.destinationDistance &&
                                  !item.outcomes[0].occupiedBy &&
                                  best.outcomes[0].occupiedBy
                              ) {
                                  return item;
                              }

                              return best;
                          },
                          null,
                      );

                      return (
                          freeCandidate ||
                          collisionCandidate ||
                          fallbackCandidate
                      );
                  })()
                : candidates.reduce((best, item) => {
                      if (!best) {
                          return item;
                      }

                      if (item.exactCount !== best.exactCount) {
                          return item.exactCount > best.exactCount
                              ? item
                              : best;
                      }

                      if (item.worstScore !== best.worstScore) {
                          return item.worstScore < best.worstScore
                              ? item
                              : best;
                      }

                      if (item.averageScore !== best.averageScore) {
                          return item.averageScore < best.averageScore
                              ? item
                              : best;
                      }

                      if (item.afterOneCount !== best.afterOneCount) {
                          return item.afterOneCount > best.afterOneCount
                              ? item
                              : best;
                      }

                      if (item.beforeOneCount !== best.beforeOneCount) {
                          return item.beforeOneCount < best.beforeOneCount
                              ? item
                              : best;
                      }

                      return item.destinationDistance < best.destinationDistance
                          ? item
                          : best;
                  }, null);

        const knownOutcome = candidate.outcomes[0];
        const isKnownCard = sourceRows.length === 1;
        const sourcePositions = sourceRows.map(
            (row) => sourcePositionByCode[row.finalCode],
        );

        const sourceSideText =
            sourcePositions.length === 0
                ? ""
                : sourcePositions.every((position) => position < target)
                  ? "Todas las posibles estaban antes del objetivo."
                  : sourcePositions.every((position) => position > target)
                    ? "Todas las posibles estaban después del objetivo."
                    : sourcePositions.every((position) => position === target)
                      ? "Todas las posibles estaban justo en el objetivo."
                      : "Las posibles están repartidas antes y después del objetivo.";

        const simulationRows = isKnownCard
            ? [
                  ...assignedRows
                      .filter(
                          (row) => row.finalCode !== sourceRows[0].finalCode,
                      )
                      .map((row) => ({
                          id: row.finalCode,
                          value: cardsByCode[row.finalCode],
                          code: row.finalCode,
                          originalCode: row.finalCode,
                          isSelected: false,
                          isCollision:
                              row.finalCode === candidate.row.finalCode,
                      })),
                  {
                      id: `${sourceRows[0].finalCode}-selected`,
                      value: cardsByCode[sourceRows[0].finalCode],
                      code: candidate.row.finalCode,
                      originalCode: sourceRows[0].finalCode,
                      isSelected: true,
                      isCollision: Boolean(knownOutcome.occupiedBy),
                  },
              ]
                  .sort((a, b) => {
                      const positionDiff =
                          countedIndexByCode[a.code] -
                          countedIndexByCode[b.code];

                      if (positionDiff !== 0) {
                          return positionDiff;
                      }

                      return a.isSelected ? -1 : 1;
                  })
                  .map((card, index) => ({
                      ...card,
                      finalPosition: index + 1,
                      isTargetPosition: index + 1 === target,
                  }))
            : [];

        return {
            target,
            countModeLabel: countMode === "topCara" ? "Top cara" : "Top dorso",
            countModeExplanation:
                countMode === "topCara"
                    ? "Se cuenta desde la cara, de arriba hacia abajo en la vista de cara."
                    : "Se cuenta desde el dorso, de arriba hacia abajo en la vista de dorso.",
            sourceSideText,
            possiblePositionsText: [...new Set(candidate.possiblePositions)]
                .sort((a, b) => a - b)
                .join(" / "),
            candidate,
            simulationRows,
            sourceRows,
            isKnownCard,
            selectedText: isKnownCard
                ? cardsByCode[sourceRows[0].finalCode]
                : `Carta desconocida (${sourceRows.length} posibles)`,
            selectedCode: isKnownCard ? sourceRows[0].finalCode : "",
            casesToShow: candidate.outcomes.slice(0, 24),
            hiddenCasesCount: Math.max(candidate.outcomes.length - 24, 0),
            isExactFree:
                isKnownCard &&
                !knownOutcome.occupiedBy &&
                knownOutcome.firstPosition === target,
            isCollision:
                isKnownCard &&
                Boolean(knownOutcome.occupiedBy) &&
                knownOutcome.firstPosition === target,
        };
    }, [
        rows,
        cardsByCode,
        selectedRank,
        selectedSuit,
        selectedMarkA,
        selectedMarkB,
        targetPosition,
        countMode,
    ]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Calcular dadas para mover una carta a otra posición
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Marca la información que sabes de la carta. Puede empezar
                    como carta totalmente desconocida y mejorar el cálculo al
                    saber el valor, el palo o alguna marca.
                </Typography>

                <Stack spacing={2}>
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mb: 0.75 }}
                        >
                            Carta marcada
                        </Typography>

                        <Stack spacing={1}>
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.25 }}
                                >
                                    Valor
                                </Typography>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={selectedRank}
                                    onChange={handleRankChange}
                                    sx={{ flexWrap: "wrap" }}
                                >
                                    <ToggleButton
                                        value=""
                                        sx={{ textTransform: "none" }}
                                    >
                                        ?
                                    </ToggleButton>

                                    {CARD_RANKS.map((rank) => (
                                        <ToggleButton
                                            key={rank}
                                            value={rank}
                                            sx={{
                                                textTransform: "none",
                                                minWidth: 38,
                                            }}
                                        >
                                            {rank}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.25 }}
                                >
                                    Palo
                                </Typography>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={selectedSuit}
                                    onChange={handleSuitChange}
                                >
                                    <ToggleButton
                                        value=""
                                        sx={{ textTransform: "none" }}
                                    >
                                        ?
                                    </ToggleButton>

                                    {CARD_SUITS.map((suit) => (
                                        <ToggleButton
                                            key={suit.value}
                                            value={suit.value}
                                            sx={{
                                                color: suit.color,
                                                fontWeight: 700,
                                                fontSize: 18,
                                                minWidth: 46,
                                            }}
                                        >
                                            {suit.value}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.25 }}
                                >
                                    Marca 1
                                </Typography>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={selectedMarkA}
                                    onChange={handleMarkAChange}
                                    sx={{ flexWrap: "wrap" }}
                                >
                                    <ToggleButton
                                        value=""
                                        sx={{ textTransform: "none" }}
                                    >
                                        ?
                                    </ToggleButton>

                                    {markLetters.map((letter) => (
                                        <ToggleButton
                                            key={letter}
                                            value={letter}
                                            sx={{
                                                textTransform: "none",
                                                minWidth: 38,
                                            }}
                                        >
                                            {currentPictograms[letter] ||
                                                letter}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            {markCount >= 2 ? (
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 0.25 }}
                                    >
                                        Marca 2
                                    </Typography>

                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={selectedMarkB}
                                        onChange={handleMarkBChange}
                                        sx={{ flexWrap: "wrap" }}
                                    >
                                        <ToggleButton
                                            value=""
                                            sx={{ textTransform: "none" }}
                                        >
                                            ?
                                        </ToggleButton>

                                        {markLetters.map((letter) => (
                                            <ToggleButton
                                                key={letter}
                                                value={letter}
                                                sx={{
                                                    textTransform: "none",
                                                    minWidth: 38,
                                                }}
                                            >
                                                {currentPictograms[letter] ||
                                                    letter}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                            ) : null}
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={countMode}
                            onChange={(_, value) =>
                                value && setCountMode(value)
                            }
                        >
                            <ToggleButton
                                value="topDorso"
                                sx={{ textTransform: "none" }}
                            >
                                Top dorso
                            </ToggleButton>

                            <ToggleButton
                                value="topCara"
                                sx={{ textTransform: "none" }}
                            >
                                Top cara
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={positionBase}
                            onChange={(_, value) =>
                                value && setPositionBase(value)
                            }
                            sx={{ flexWrap: "wrap" }}
                        >
                            {POSITION_BASES.map((rank) => (
                                <ToggleButton
                                    key={rank}
                                    value={rank}
                                    sx={{
                                        textTransform: "none",
                                        minWidth: 38,
                                    }}
                                >
                                    {rank}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={positionUnits}
                            onChange={(_, value) =>
                                value && setPositionUnits(value)
                            }
                            sx={{ flexWrap: "wrap" }}
                        >
                            {POSITION_DIGITS.map((rank) => (
                                <ToggleButton
                                    key={rank}
                                    value={rank}
                                    sx={{
                                        textTransform: "none",
                                        minWidth: 38,
                                    }}
                                >
                                    {rank}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {result?.error ? (
                        <Alert severity="warning">{result.error}</Alert>
                    ) : result ? (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                            <Stack spacing={0.75}>
                                <Typography variant="h4">
                                    {codeToPictogram(
                                        result.candidate.row.finalCode,
                                        currentPictograms,
                                    )}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Carta:</strong>{" "}
                                    {result.selectedText}
                                </Typography>

                                {result.isKnownCard ? (
                                    <Typography variant="body2">
                                        <strong>Código actual:</strong>{" "}
                                        {result.selectedCode}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2">
                                        <strong>Información:</strong>{" "}
                                        {result.sourceRows.length} cartas
                                        posibles. {result.sourceSideText}
                                    </Typography>
                                )}

                                <Typography variant="body2">
                                    <strong>Debe apuntar a:</strong>{" "}
                                    {result.candidate.row.finalCode}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Número:</strong>{" "}
                                    {codeToNumeric(
                                        result.candidate.row.finalCode,
                                        safePackets,
                                    )}
                                </Typography>

                                <Typography variant="body2">
                                    <strong>Resultado:</strong>{" "}
                                    {result.possiblePositionsText} desde{" "}
                                    {result.countModeLabel}
                                </Typography>

                                {result.isKnownCard && result.isExactFree ? (
                                    <Alert severity="success" sx={{ mt: 0.5 }}>
                                        Caso ideal: esa posición radical está
                                        libre y la carta cae exactamente en la
                                        posición {result.target}.
                                    </Alert>
                                ) : result.isKnownCard && result.isCollision ? (
                                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                                        No hay hueco limpio en esa posición.
                                        Apunta al mismo sitio que{" "}
                                        {
                                            result.candidate.outcomes[0]
                                                .occupiedBy
                                        }
                                        , así que quedará en la posición{" "}
                                        {result.possiblePositionsText}.
                                    </Alert>
                                ) : result.candidate.exactCount ===
                                  result.sourceRows.length ? (
                                    <Alert severity="success" sx={{ mt: 0.5 }}>
                                        Con lo que sabes, todos los casos
                                        posibles incluyen la posición{" "}
                                        {result.target}.
                                    </Alert>
                                ) : result.candidate.exactCount > 0 ? (
                                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                                        Hay incertidumbre:{" "}
                                        {result.candidate.exactCount} de{" "}
                                        {result.sourceRows.length} casos
                                        posibles incluyen la posición exacta. El
                                        resto queda cerca.
                                    </Alert>
                                ) : (
                                    <Alert severity="info" sx={{ mt: 0.5 }}>
                                        No hay una garantía exacta con la
                                        información actual. Esta es la mejor
                                        salida global que he encontrado.
                                    </Alert>
                                )}
                            </Stack>

                            <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                    setIsSimulationOpen((prev) => !prev)
                                }
                                endIcon={
                                    isSimulationOpen ? (
                                        <Icons.UnfoldLess />
                                    ) : (
                                        <Icons.UnfoldMore />
                                    )
                                }
                                sx={{
                                    alignSelf: "flex-start",
                                    textTransform: "none",
                                    mt: 0.5,
                                }}
                            >
                                Revisar simulación
                            </Button>

                            <Collapse in={isSimulationOpen} unmountOnExit>
                                <Paper
                                    variant="outlined"
                                    sx={{ p: 1.25, mt: 1 }}
                                >
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Simulación compactada desde{" "}
                                                {result.countModeLabel}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                            >
                                                {result.countModeExplanation}
                                            </Typography>

                                            {result.isKnownCard &&
                                            result.isCollision ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: "block",
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    Hay colisión: dos cartas
                                                    apuntan al mismo código. Se
                                                    muestran juntas; el orden
                                                    exacto entre ambas depende
                                                    del orden físico inicial.
                                                </Typography>
                                            ) : null}
                                        </Box>

                                        {result.isKnownCard ? (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.75,
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        alignSelf: "center",
                                                        mr: 0.5,
                                                    }}
                                                >
                                                    {countMode === "topCara"
                                                        ? "Top cara"
                                                        : "Top dorso"}
                                                </Typography>

                                                {result.simulationRows.map(
                                                    (card) => (
                                                        <Paper
                                                            key={card.id}
                                                            variant="outlined"
                                                            sx={{
                                                                p: 0.6,
                                                                borderRadius: 1.2,
                                                                borderColor:
                                                                    card.isSelected
                                                                        ? "primary.main"
                                                                        : card.isTargetPosition
                                                                          ? "success.main"
                                                                          : "divider",
                                                                bgcolor:
                                                                    card.isSelected
                                                                        ? "action.selected"
                                                                        : "background.paper",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    fontWeight: 700,
                                                                    lineHeight: 1.1,
                                                                    color: getCardTextColor(
                                                                        card.value,
                                                                    ),
                                                                    overflow:
                                                                        "hidden",
                                                                    textOverflow:
                                                                        "ellipsis",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {card.value}
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    fontFamily:
                                                                        "monospace",
                                                                    fontSize: 10,
                                                                    lineHeight: 1.1,
                                                                }}
                                                            >
                                                                {card.originalCode ===
                                                                card.code
                                                                    ? card.code
                                                                    : `${card.originalCode} → ${card.code}`}
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    fontWeight:
                                                                        card.isTargetPosition
                                                                            ? 700
                                                                            : 400,
                                                                    fontSize: 10,
                                                                    lineHeight: 1.1,
                                                                }}
                                                            >
                                                                #
                                                                {
                                                                    card.finalPosition
                                                                }
                                                                {card.isTargetPosition
                                                                    ? " objetivo"
                                                                    : ""}
                                                            </Typography>
                                                        </Paper>
                                                    ),
                                                )}

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        alignSelf: "center",
                                                        ml: 0.5,
                                                    }}
                                                >
                                                    {countMode === "topCara"
                                                        ? "Bottom cara"
                                                        : "Bottom dorso"}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Stack spacing={0.75}>
                                                {result.casesToShow.map(
                                                    (outcome) => (
                                                        <Paper
                                                            key={
                                                                outcome.sourceCode
                                                            }
                                                            variant="outlined"
                                                            sx={{
                                                                p: 0.75,
                                                                borderColor:
                                                                    outcome.positions.includes(
                                                                        result.target,
                                                                    )
                                                                        ? "success.main"
                                                                        : "divider",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    fontWeight: 700,
                                                                    color: getCardTextColor(
                                                                        outcome.sourceText,
                                                                    ),
                                                                }}
                                                            >
                                                                {
                                                                    outcome.sourceText
                                                                }
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                }}
                                                            >
                                                                Estaba #
                                                                {
                                                                    outcome.sourcePosition
                                                                }{" "}
                                                                · código{" "}
                                                                {
                                                                    outcome.sourceCode
                                                                }{" "}
                                                                →{" "}
                                                                {
                                                                    result
                                                                        .candidate
                                                                        .row
                                                                        .finalCode
                                                                }
                                                            </Typography>

                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                }}
                                                            >
                                                                Resultado:{" "}
                                                                {[
                                                                    ...new Set(
                                                                        outcome.positions,
                                                                    ),
                                                                ]
                                                                    .sort(
                                                                        (
                                                                            a,
                                                                            b,
                                                                        ) =>
                                                                            a -
                                                                            b,
                                                                    )
                                                                    .join(
                                                                        " / ",
                                                                    )}
                                                            </Typography>
                                                        </Paper>
                                                    ),
                                                )}

                                                {result.hiddenCasesCount > 0 ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Y{" "}
                                                        {
                                                            result.hiddenCasesCount
                                                        }{" "}
                                                        casos posibles más.
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        )}
                                    </Stack>
                                </Paper>
                            </Collapse>
                        </Paper>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RadicalMoveDialog;
